/**
 * reportComputations.js
 * Pure (no-React) data computation functions used by both ReportModals and the PDF generator.
 */
import { computeHonKitzba } from "./honKitzba";

const RETZEF = { "1": "כן", "2": "לא", "3": "אין מידע" };

export function getFundClass(f) {
  const CAPITAL_CATS = ["קרן השתלמות", "קופת גמל להשקעה", "חיסכון לכל ילד"];
  if (CAPITAL_CATS.includes(f.cat) || f.sugMutzar === "9") return "הון";
  
  const sugKupa = f.retirementProjections?.[0]?.kupot?.[0]?.sugKupa || "";
  if (sugKupa.includes("לא משלמת")) return "קצבה לא משלמת";
  if (sugKupa.includes("משלמת")) return "קצבה משלמת";
  if (f.cat === "קרן פנסיה" || f.cat === "ביטוח מנהלים") return "קצבה משלמת";
  if (f.cat === "קופת גמל") return "קצבה לא משלמת";
  return "הון";
}

// כל 12 שדות הפיצויים/רצפים לקרן בודדת — משמש גם את computePitzuyimRows (ריכוז
// רב-קרנות למודאל) וגם את PitzuyimTab (טאב פר-קרן), כדי ששני המקומות יציגו
// בדיוק אותם נתונים לפי אותה לוגיקה.
export function pitzuyimFieldsForFund(f) {
  const p = f.yitrot?.pitzuyim || {};
  const pitzuyimPerut = f.yitrot?.perut?.filter(y => y.sugHafrasha === "פיצויים") || [];
  const pitzuyimRedeem = pitzuyimPerut.reduce((s, y) => s + (y.redeem || 0), 0);

  const type = getFundClass(f);
  const erkeiMaasikNochechi = p.erkeiPitzuimMaasik || pitzuyimRedeem || 0;
  const llotHitchashbenot = p.yitratPitzuim || 0;

  const { honPitzuyim, kitzbaPitzuyim } = computeHonKitzba(f);
  const hasPeriodSplit = honPitzuyim > 0 || kitzbaPitzuyim > 0;

  return {
    company:   f.companyName,
    polNum:    f.polNum,
    employerName: f.employer?.name || "—",
    tochnit:   f.tochnit,
    cat:       f.cat,
    honAmount: hasPeriodSplit ? honPitzuyim
               : (type === "הון" ? erkeiMaasikNochechi : 0),
    kitzbaMeshAmount: hasPeriodSplit ? (type === "קצבה משלמת" ? kitzbaPitzuyim : 0)
               : (type === "קצבה משלמת" ? erkeiMaasikNochechi : 0),
    kitzbaLoMeshAmount: hasPeriodSplit ? (type !== "קצבה משלמת" ? kitzbaPitzuyim : 0)
               : (type === "קצבה לא משלמת" ? erkeiMaasikNochechi : 0),
    pturimMaavidimKodmim:  p.pitzuyimPturim        || 0,
    lekitzba:              p.pitzuyimLekitzba       || 0,
    beretzefKitzba:        p.pitzuyimBeretzefKitzba || 0,
    beretzefZechuyot:      parseFloat(f.yitrot?.raw?.TZVIRAT_PITZUIM_MAAVIDIM_KODMIM_BERETZEF_ZECHUYOT || 0),
    pitzuyim1999:          parseFloat(f.yitrot?.raw?.TZVIRAT_PITZUIM_31_12_1999_LEKITZBA || 0),
    erkeiMaasikNochechi,
    markivLemas:           p.erkeiPitzuyimMas       || 0,
    retzefZehuyut:         parseFloat(f.yitrot?.raw?.ERECH_PIDION_PITZUIM_MAAVIDIM_KODMIM_RETZEF_ZEHUYUT || 0),
    lehon:                 parseFloat(f.yitrot?.raw?.ERECH_PIDION_PITZUIM_LEHON_MAAVIDIM_KODMIM || 0),
    llotHitchashbenot,
    retzefPitzuyimKitzba:  RETZEF[f.yitrot?.raw?.KAYAM_RETZEF_PITZUIM_KITZBA] || "—",
    retzefZechuyot:        RETZEF[f.yitrot?.raw?.KAYAM_RETZEF_ZECHUYOT_PITZUIM] || "—",
  };
}

export function computePitzuyimRows(funds) {
  return funds
    .filter(f => f.yitrot?.pitzuyim || f.yitrot?.perut?.some(p => p.sugHafrasha === "פיצויים"))
    .map(pitzuyimFieldsForFund)
    .filter(r => r.erkeiMaasikNochechi > 0 || r.llotHitchashbenot > 0 || r.pturimMaavidimKodmim > 0);
}

// פירוק הון לפי סוג (פר-קרן + סיכומים). מקור אמת יחיד לכרטיס "ריכוז כספים בהון",
// לגרף הון/קצבה בדאשבורד, ולסעיף "כספים להון" בדוח ה-PDF.
export function computeHonBreakdown(funds) {
  const rows = (funds || []).map(f => {
    const hk = computeHonKitzba(f);
    const isStudy = f.cat === "קרן השתלמות";
    return {
      f,
      tagmulimHon: isStudy ? 0 : (hk.hon || 0),
      hashtalamut: isStudy ? (hk.hon || 0) : 0,
      tikun190:    hk.hon190 || 0,
      pitzuyimHon: hk.honPitzuyim || 0,
      kitzbaUntil2000: isStudy ? 0 : ((hk.kitzbaPreSettlement || 0) + (hk.preReform || 0)),
    };
  });
  const totals = rows.reduce((a, r) => ({
    tagmulimHon: a.tagmulimHon + r.tagmulimHon,
    hashtalamut: a.hashtalamut + r.hashtalamut,
    tikun190:    a.tikun190    + r.tikun190,
    pitzuyimHon: a.pitzuyimHon + r.pitzuyimHon,
    kitzbaUntil2000: (a.kitzbaUntil2000 || 0) + r.kitzbaUntil2000,
  }), { tagmulimHon: 0, hashtalamut: 0, tikun190: 0, pitzuyimHon: 0, kitzbaUntil2000: 0 });
  return { rows, totals };
}

export function computeTagmulimRows(funds) {
  const result = [];
  funds.forEach(f => {
    if (f.cat === "קרן השתלמות") return;
    if (!f.yitrot?.perut?.length) return;
    const hasTagmulim = f.yitrot.perut.some(p => p.sugHafrasha !== "פיצויים");
    if (!hasTagmulim) return;

    let tagmulim = 0, pitzuyim = 0, ishi = 0;
    f.yitrot.perut.forEach(p => {
      if (p.sugHafrasha === "פיצויים") pitzuyim += p.redeem || 0;
      else if (p.sugHafrasha === "תגמולים עובד" || p.sugHafrasha === "תגמולים מעביד" ||
               p.sugHafrasha === "תגמולים 47"   || p.sugHafrasha === 'קה"ש עובד' ||
               p.sugHafrasha === 'קה"ש מעביד') tagmulim += p.redeem || 0;
      else if (p.sugHafrasha === "תגמולים פרט") ishi += p.redeem || 0;
    });

    const pension = f.retirementProjections?.[0]?.kupot?.[0]?.pensionMonthly
                 || f.retirementProjections?.[0]?.kupot?.[0]?.pension;
    const type = getFundClass(f);
    const totalRedeem = tagmulim + ishi;
    const hk = computeHonKitzba(f);

    result.push({
      company:      f.companyName,
      polNum:       f.polNum,
      tochnit:      f.tochnit,
      cat:          f.cat,
      kupaType:     type,
      totalRedeem,
      tagmulim,
      pitzuyim,
      ishi,
      pension:      pension ? parseFloat(pension) : 0,
      hon:          hk.hon,
      preReform:    hk.preReform || 0,
      kitzbaMesh:   type === "קצבה משלמת"    ? hk.kitzba : 0,
      kitzbaLoMesh: type === "קצבה לא משלמת" ? hk.kitzba : 0,
      tsua:         f.returns?.neto || f.returns?.bruto1 || 0,
    });
  });
  return result.sort((a, b) => b.totalRedeem - a.totalRedeem);
}
